'use client'

import { type ChangeEvent, useCallback, useMemo, useState } from 'react'

import type { CardData } from '../../../types'
import { CardInstallation } from '../../CardInstallation'
import { LibrariesOnboarding } from '../../LibrariesOnboarding'
import { CardHeader } from '../CardHeader'
import { SearchFilterBar } from '../SearchFilter'

import { RootViewList } from './RootViewList'

import styles from './RootView.module.scss'

export function RootView({
  libraryData,
  installationCardKey,
  technologies,
  suggestedTechnologies,
  selectedTechnology,
  installationTabs,
  graftabilityReport,
  onTechnologyChange,
  onConnectionStringGenerator,
  mobileHeaderInfo,
  list,
  path,
  onMobileBack,
}: CardData) {
  const [searchQuery, setSearchQuery] = useState('')

  const handleSearchChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value)
  }, [])

  const handleSearchClear = useCallback(() => {
    setSearchQuery('')
  }, [])

  const isEmptyRoot = path.length === 0 && list.length === 0

  const filteredList = useMemo(() => {
    if (!searchQuery.trim()) return list
    const query = searchQuery.toLowerCase()
    return list.filter((card) => {
      const label = typeof card.children === 'string' ? card.children : ''
      const title = 'title' in card && typeof card.title === 'string' ? card.title : ''
      return (
        label.toLowerCase().includes(query) ||
        title.toLowerCase().includes(query) ||
        card.id.toLowerCase().includes(query)
      )
    })
  }, [list, searchQuery])

  return (
    <>
      <div className={styles.header}>
        {path.length > 0 && libraryData && (
          <CardInstallation
            installationCardKey={installationCardKey}
            technologies={technologies}
            suggestedTechnologies={suggestedTechnologies}
            selectedTechnology={selectedTechnology}
            tabs={installationTabs}
            libraryData={libraryData}
            graftabilityReport={graftabilityReport}
            onTechnologyChange={onTechnologyChange}
            onConnectionStringGenerator={onConnectionStringGenerator}
          />
        )}
        <CardHeader
          label={mobileHeaderInfo.label}
          count={list.length}
          onBack={path.length > 0 ? onMobileBack : undefined}
        />
      </div>
      {isEmptyRoot ? (
        <LibrariesOnboarding />
      ) : (
        <>
          <SearchFilterBar
            searchQuery={searchQuery}
            placeholder="Search libraries..."
            onSearchChange={handleSearchChange}
            onSearchClear={handleSearchClear}
            filterButtonDisabled
          />
          <RootViewList searchQuery={searchQuery} filteredList={filteredList} />
        </>
      )}
    </>
  )
}
